import type { ArtistProfileKind } from 'fym-shared/src/music24/artistProfile';
import type { SMLRootParamList, SMLRouteNames } from '../../../../routing/sml/SMLRoutes';

/** The query of Studio → Profile (`promote/promoteProfile`). */
export type ProfileRouteParams = NonNullable<SMLRootParamList[SMLRouteNames.promoteProfile]>;

/** A tool of the Profile page (design artist-profile b1–b4, c1). */
export const PROFILE_TOOLS = ['spotifyHeader', 'spotifyAvatar', 'banners', 'pressPhoto', 'bio'] as const;
export type ProfileTool = (typeof PROFILE_TOOLS)[number];

/** A tab of the banners tool (design b2). */
export const PROFILE_BANNER_TABS = ['youtubeBanner', 'facebookCover'] as const;
export type ProfileBannerKind = (typeof PROFILE_BANNER_TABS)[number];

/** An image tool: every tool but the bio. */
export type ProfileImageTool = Exclude<ProfileTool, 'bio'>;

/**
 * A sheet over the Profile: "Send to all" (`sendAll`, design profile-card c), the same sheet with
 * only Spotify ticked (`spotify`, the Spotify upload), or the bio upload to Spotify (`spotifyBio`).
 */
export const PROFILE_SHEETS = ['spotify', 'spotifyBio', 'sendAll'] as const;
export type ProfileSheet = (typeof PROFILE_SHEETS)[number];

/** What the URL of the Profile shows: the page, a tool, an upload sheet. */
export interface ProfileRouteState {
  tool: ProfileTool | null;
  /** The tab of the banners tool; YouTube when the URL names none. */
  banner: ProfileBannerKind;
  sheet: ProfileSheet | null;
}

function isProfileSheet(value: string | null): value is ProfileSheet {
  return value !== null && (PROFILE_SHEETS as readonly string[]).includes(value);
}

function isProfileTool(value: string | null): value is ProfileTool {
  return value !== null && (PROFILE_TOOLS as readonly string[]).includes(value);
}

function isBannerKind(value: string | null): value is ProfileBannerKind {
  return value !== null && (PROFILE_BANNER_TABS as readonly string[]).includes(value);
}

/** The view of a Profile URL. An unknown `tool`, `banner` or `sheet` is ignored. Pure. */
export function profileRouteState(search: string | URLSearchParams): ProfileRouteState {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const tool = params.get('tool');
  const banner = params.get('banner');
  const sheet = params.get('sheet');
  return {
    tool: isProfileTool(tool) ? tool : null,
    banner: isBannerKind(banner) ? banner : 'youtubeBanner',
    sheet: isProfileSheet(sheet) ? sheet : null,
  };
}

/** The tool that makes a kind: the two banners share one tool. Pure. */
export function profileToolOfKind(kind: ArtistProfileKind): ProfileImageTool {
  if (kind === 'youtubeBanner' || kind === 'facebookCover') {
    return 'banners';
  }
  return kind;
}

/** The query that opens a tool (and the tab of a banner kind). Pure. */
export function profileToolParams(tool: ProfileTool, banner?: ProfileBannerKind): ProfileRouteParams {
  return tool === 'banners' && banner !== undefined ? { tool, banner } : { tool };
}

/** The query that opens the tool of a kind. Pure. */
export function profileKindParams(kind: ArtistProfileKind): ProfileRouteParams {
  const tool = profileToolOfKind(kind);
  return tool === 'banners' ? profileToolParams(tool, kind as ProfileBannerKind) : profileToolParams(tool);
}
